import { request } from './request';
import {
    LoginPayload,
    LoginResponse,
    Account,
    NewAccountPayload,
    AdvancedOptions,
    DisposableToken,
    AccountWarnings,
    CrcDecision,
    CrcFormPayload,
    CrcProfile,
    PrivateIPCollection,
    MarketingOptions,
    BrandingInfoPayload,
    BrandingInfo,
    ResetEmailPayload,
    GoogleTagPayload,
} from '@dev/ee-api/ee-api-models';
import { local } from 'store2';
import { AxiosPromise } from 'axios';

export const updateMarketingOptions = (options: MarketingOptions[]): AxiosPromise<void> => {
    const { Accounts } = request();
    return Accounts.accountsMarketingoptionsPut(options);
};
/**
 * Method used to obtain access token/api key
 * @param params LoginPayload used for authorization
 */
export const login = (params: LoginPayload): Promise<LoginResponse> => {
    const { Auth } = request({ disableImpersonation: true });

    return Auth.authPost(params).then((resp): LoginResponse => {
        const browserToken = resp.headers['x-elasticemail-browsertoken'];

        if (browserToken && local.get('browserToken') !== browserToken) {
            local.set('browserToken', browserToken);
        }

        return resp.data;
    });
};

export const createAccount = (account: NewAccountPayload): AxiosPromise<Account> => {
    const { Accounts } = request({ disableImpersonation: true });

    return Accounts.accountsPost(account);
};

export const resetPassword = (sourceUrl: string, email: string): AxiosPromise<void> => {
    const { Accounts } = request();

    return Accounts.accountsPasswordGet(sourceUrl, email);
};

export const loadAccount = (): AxiosPromise<Account> => {
    const { Accounts } = request();
    return Accounts.accountsGet();
};
export const updateAdvancedOptions = (account: AdvancedOptions): AxiosPromise<AdvancedOptions> => {
    const { Accounts } = request();
    return Accounts.accountsAdvancedoptionsPut(account);
};

export const updateGlobalContacts = (globaloptions: boolean): AxiosPromise<void> => {
    const { Accounts } = request();
    return Accounts.accountsGlobalcontactsPut(globaloptions);
};

export const getDisposableToken = (): AxiosPromise<DisposableToken> => {
    const { Accounts } = request();

    return Accounts.accountsDisposabletokenGet();
};

export const purchasePrivateIP = (region: string, quantity: number): AxiosPromise<PrivateIPCollection> => {
    const { Accounts } = request();
    return Accounts.accountsPurchaseprivateipPost(region, quantity);
};

export const sendCrcFormParams = (crcParams: CrcFormPayload, subaccountEmail?: string): AxiosPromise<boolean> => {
    const { Accounts } = request();
    return Accounts.accountsCrcFormPost(crcParams, subaccountEmail);
};

export const validateCRCForm = (crcDecision: CrcDecision, subaccountEmail: string): AxiosPromise<void> => {
    const { Accounts } = request();
    return Accounts.accountsCrcValidationPost(crcDecision, subaccountEmail);
};

export const getAccountsName = (subaccountEmail: string): AxiosPromise<CrcProfile> => {
    const { Accounts } = request();
    return Accounts.accountsCrcProfileGet(subaccountEmail);
};

export const deleteAccount = (password: string): AxiosPromise<void> => {
    const { Accounts } = request();
    return Accounts.accountsDelete(password);
};

export const getAccountWarnings = (): AxiosPromise<AccountWarnings[]> => {
    const { Accounts } = request();
    return Accounts.accountsWarningsGet();
};

export const setNewPassword = (VerificationKey: string, Password: string): AxiosPromise<void> => {
    const { Accounts } = request();

    return Accounts.accountsPasswordPost({ VerificationKey, Password });
};

export const getCustomBranding = (brandingPayload: BrandingInfoPayload): AxiosPromise<BrandingInfo> => {
    const { Accounts } = request();
    return Accounts.accountsBrandingPost(brandingPayload);
};

export const purchaseExtendedLogsPOST = (): AxiosPromise<void> => {
    const { Accounts } = request();
    return Accounts.accountsPurchaseextendedlogsPost();
};

export const setValidSenderDomainOnly = (validSenderDomainOnly: boolean): AxiosPromise<void> => {
    const { Accounts } = request();
    return Accounts.accountsValidsenderdomainonlyByValidsenderdomainonlyPut(validSenderDomainOnly);
};

export const resetAccountEmailPUT = ({ VerificationKey, NewEmail }: ResetEmailPayload): AxiosPromise<void> => {
    const { Accounts } = request();
    return Accounts.accountsResetemailPut({ VerificationKey, NewEmail });
};

export const googleTagsGET = () => {
    const { Accounts } = request();

    return Accounts.accountsGoogletagsGet().then((resp) => resp?.data);
};

export const googleTagsPOST = (payload: GoogleTagPayload) => {
    const { Accounts } = request();

    return Accounts.accountsGoogletagsPost(payload);
};
